/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { MailIcon, UserPlusIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useId, useState } from "react"
import { useNavigate } from "react-router"

import { OrganizationViewModel } from "@/features/organization/organization-viewmodel"
import { userDetailLocation } from "@/features/user/user-detail-location"
import { UserListViewModel } from "@/features/user/user-list-viewmodel"
import { LoggerViewModel } from "@/logger/logger-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { PaginationControls } from "../components/pagination-controls"
import { Button } from "../primitives/button"
import { AllUsersSelectOrgView } from "./all-users-select-org-view"
import { UserCreateModal } from "./user-create-modal"
import { UserFilters } from "./user-filters"
import { UserInviteModal } from "./user-invite-modal"
import { UserTable } from "./user-table"

/**
 * Lists the users of the selected organization, with filters and pagination.
 *
 * An admin with more than one organization picks one first. The route loads
 * the users; the view shows skeleton rows while they load.
 */
export const AllUsersView = observer(() => {
    const headingId = useId()
    const listVM = useViewModel(UserListViewModel)
    const orgVM = useViewModel(OrganizationViewModel)
    const loggerVM = useViewModel(LoggerViewModel)
    const navigate = useNavigate()

    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showInviteModal, setShowInviteModal] = useState(false)

    const selectedOrgId = orgVM.selectedOrgId

    const handleOrgSelected = async (orgId: number) => {
        await listVM.getUsers(orgId)
        orgVM.setSelectedOrgId(orgId)
    }

    const handleChangeOrganization = () => {
        orgVM.setSelectedOrgId("none")
        listVM.resetFilters()
    }

    const openModal = (setOpen: (open: boolean) => void) => {
        if (selectedOrgId === "none") {
            loggerVM.logErrorMessage("Please select a specific organization first.")
        } else {
            setOpen(true)
        }
    }

    // Admins with a single organization skip this: the route already loads its users.
    if (orgVM.hasMultipleOrganizations && selectedOrgId === "none") {
        return <AllUsersSelectOrgView organizations={orgVM.organizations} onOrganizationSelected={handleOrgSelected} />
    }

    return (
        <div className="w-content">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div className="grid gap-1">
                    <h1 className="m-0 text-3xl font-bold text-primary" id={headingId}>
                        Users
                    </h1>
                    <p className="m-0 flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
                        <span>of {orgVM.selectedOrgName}</span>
                        {orgVM.hasMultipleOrganizations && (
                            <Button variant="link" className="h-auto p-0" onClick={handleChangeOrganization}>
                                Change organization
                            </Button>
                        )}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Button className="font-semibold uppercase" onClick={() => openModal(setShowCreateModal)}>
                        <UserPlusIcon data-icon="inline-start" />
                        Create User
                    </Button>
                    <Button className="font-semibold uppercase" onClick={() => openModal(setShowInviteModal)}>
                        <MailIcon data-icon="inline-start" />
                        Invite External User
                    </Button>
                </div>
            </header>

            <UserFilters />

            <UserTable labelledBy={headingId} />

            {!listVM.isLoadingUsers && (
                <PaginationControls
                    currentPage={listVM.currentPage}
                    totalPages={listVM.totalPages}
                    onPageChange={(page) => listVM.setPage(page)}
                    className="mt-6"
                />
            )}

            {showCreateModal && selectedOrgId !== "none" && (
                <UserCreateModal
                    orgId={selectedOrgId}
                    orgName={orgVM.selectedOrgName}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={(userId) => {
                        setShowCreateModal(false)
                        navigate(userDetailLocation(userId, selectedOrgId))
                    }}
                />
            )}

            {showInviteModal && selectedOrgId !== "none" && (
                <UserInviteModal
                    orgId={selectedOrgId}
                    orgName={orgVM.selectedOrgName}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={() => {
                        setShowInviteModal(false)
                        void listVM.getUsers(selectedOrgId)
                    }}
                />
            )}
        </div>
    )
})
